import React, { useState, useEffect } from 'react';
import { 
  Bell, Mail, CheckCircle, XCircle, AlertCircle, Clock, 
  ShoppingBag, Package, Users, DollarSign, Star, MessageSquare,
  Settings, TrendingUp, Truck, Heart, Calendar, Eye,
  ChevronLeft, ChevronRight, Download, Filter, Trash2,
  CheckCheck, BellOff, Volume2, VolumeX, Archive, RefreshCw, Search
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Notifications = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [notifications, setNotifications] = useState([]);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const itemsPerPage = 8;

  // Static Mock Data
  const notificationData = [
    {
      id: 'NOT-001',
      title: 'New Order Received',
      message: 'Order #ORD-7829 has been placed by Rahul Sharma worth ₹2,450',
      type: 'order',
      priority: 'high',
      date: 'Oct 24, 2024',
      time: '10:30 AM',
      read: false,
      icon: ShoppingBag,
      actionLink: '/admin/orders/ORD-7829'
    },
    {
      id: 'NOT-002',
      title: 'New Seller Registration',
      message: 'Green Thumb Nursery has requested to become a seller. Review their application.',
      type: 'seller',
      priority: 'high',
      date: 'Oct 23, 2024',
      time: '02:15 PM',
      read: false,
      icon: Users,
      actionLink: '/admin/sellers/pending'
    },
    {
      id: 'NOT-003',
      title: 'Product Review Alert',
      message: 'Amit Singh left a 5-star review on Monstera Deliciosa.',
      type: 'review',
      priority: 'medium',
      date: 'Oct 23, 2024',
      time: '11:45 AM',
      read: true,
      icon: Star,
      actionLink: '/admin/reviews'
    },
    {
      id: 'NOT-004',
      title: 'Low Stock Alert',
      message: 'Fiddle Leaf Fig stock is low (12 items left). Restock soon.',
      type: 'inventory',
      priority: 'high',
      date: 'Oct 22, 2024',
      time: '09:20 AM',
      read: false,
      icon: Package,
      actionLink: '/admin/inventory'
    },
    {
      id: 'NOT-005',
      title: 'Payout Processed',
      message: 'Payout of ₹24,500 has been sent to Green Thumb Nursery.',
      type: 'payout',
      priority: 'medium',
      date: 'Oct 21, 2024',
      time: '04:30 PM',
      read: true,
      icon: DollarSign,
      actionLink: '/admin/payouts'
    },
    {
      id: 'NOT-006',
      title: 'New Customer Registered',
      message: 'Priya Patel has created a new customer account.',
      type: 'customer',
      priority: 'low',
      date: 'Oct 20, 2024',
      time: '01:00 PM',
      read: true,
      icon: Users,
      actionLink: '/admin/customers'
    },
    {
      id: 'NOT-007',
      title: 'System Update Complete',
      message: 'Database backup completed successfully at 02:00 AM.',
      type: 'system',
      priority: 'low',
      date: 'Oct 20, 2024',
      time: '02:15 AM',
      read: true,
      icon: Settings,
      actionLink: null
    },
    {
      id: 'NOT-008',
      title: 'Payment Failed',
      message: 'Payment for order #ORD-7831 failed. Customer has been notified.',
      type: 'payment',
      priority: 'high',
      date: 'Oct 19, 2024',
      time: '11:30 AM',
      read: false,
      icon: AlertCircle,
      actionLink: '/admin/orders/ORD-7831'
    },
    {
      id: 'NOT-009',
      title: 'Shipping Delay Alert',
      message: 'Order #ORD-7829 shipping delayed due to courier issues.',
      type: 'shipping',
      priority: 'medium',
      date: 'Oct 18, 2024',
      time: '03:45 PM',
      read: true,
      icon: Truck,
      actionLink: '/admin/orders/ORD-7829'
    },
    {
      id: 'NOT-010',
      title: 'New Message from Seller',
      message: 'City Gardens sent a message regarding payout status.',
      type: 'message',
      priority: 'medium',
      date: 'Oct 17, 2024',
      time: '10:00 AM',
      read: false,
      icon: MessageSquare,
      actionLink: '/admin/messages'
    },
  ];

  useEffect(() => {
    setNotifications(notificationData);
  }, []);

  const stats = {
    total: notifications.length,
    unread: notifications.filter(n => !n.read).length,
    read: notifications.filter(n => n.read).length,
    highPriority: notifications.filter(n => n.priority === 'high').length,
    today: notifications.filter(n => n.date === new Date().toLocaleDateString()).length
  };

  const getTypeConfig = (type) => {
    switch (type) {
      case 'order': return { label: 'Order', icon: ShoppingBag, color: '#3b82f6', bg: '#dbeafe' };
      case 'seller': return { label: 'Seller', icon: Users, color: '#8b5cf6', bg: '#e9d5ff' };
      case 'review': return { label: 'Review', icon: Star, color: '#f59e0b', bg: '#fed7aa' };
      case 'inventory': return { label: 'Inventory', icon: Package, color: '#10b981', bg: '#d1fae5' };
      case 'payout': return { label: 'Payout', icon: DollarSign, color: '#ef4444', bg: '#fee2e2' };
      case 'customer': return { label: 'Customer', icon: Users, color: '#06b6d4', bg: '#cffafe' };
      case 'system': return { label: 'System', icon: Settings, color: '#64748b', bg: '#f1f5f9' };
      case 'payment': return { label: 'Payment', icon: AlertCircle, color: '#ef4444', bg: '#fee2e2' };
      case 'shipping': return { label: 'Shipping', icon: Truck, color: '#3b82f6', bg: '#dbeafe' };
      case 'message': return { label: 'Message', icon: MessageSquare, color: '#8b5cf6', bg: '#e9d5ff' };
      default: return { label: type, icon: Bell, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const getPriorityConfig = (priority) => {
    switch (priority) {
      case 'high': return { label: 'High', icon: AlertCircle, color: '#ef4444', bg: '#fee2e2' };
      case 'medium': return { label: 'Medium', icon: Clock, color: '#f59e0b', bg: '#fed7aa' };
      default: return { label: 'Low', icon: Bell, color: '#10b981', bg: '#d1fae5' };
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          notification.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || notification.type === filterType;
    const matchesPriority = filterPriority === 'all' || notification.priority === filterPriority;
    return matchesSearch && matchesType && matchesPriority;
  });

  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
  const paginatedNotifications = filteredNotifications.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <h3>{value}</h3>
        <span>{title}</span>
      </div>
    </div>
  );

  const markAsRead = (id) => {
    setNotifications(notifications.map(notif => 
      notif.id === id ? { ...notif, read: true } : notif
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(notif => ({ ...notif, read: true })));
  };

  const deleteNotification = (id) => {
    if (window.confirm('Delete this notification?')) {
      setNotifications(notifications.filter(notif => notif.id !== id));
    }
  };

  const deleteSelected = () => {
    if (window.confirm(`Delete ${selectedNotifications.length} notifications?`)) {
      setNotifications(notifications.filter(notif => !selectedNotifications.includes(notif.id)));
      setSelectedNotifications([]);
    }
  };

  const toggleSelectNotification = (id) => {
    if (selectedNotifications.includes(id)) {
      setSelectedNotifications(selectedNotifications.filter(n => n !== id));
    } else {
      setSelectedNotifications([...selectedNotifications, id]);
    }
  };

  const selectAll = () => {
    if (selectedNotifications.length === paginatedNotifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(paginatedNotifications.map(n => n.id));
    }
  };

  const formatTimeAgo = (date, time) => {
    return `${date} at ${time}`;
  };

  return (
    <div className="admin-notifications-modern">
      {/* Header */}
      <div className="notifications-header">
        <div>
          <h1 className="page-title">Notifications</h1>
          <p className="page-subtitle">Stay updated with important events and alerts</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export
          </button>
          <button className="btn-primary" onClick={markAllAsRead}>
            <CheckCheck size={16} />
            Mark All as Read
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Notifications" value={stats.total} icon={Bell} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Unread" value={stats.unread} icon={Mail} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="High Priority" value={stats.highPriority} icon={AlertCircle} color="#ef4444" bgColor="#fee2e2" />
        <StatCard title="Today" value={stats.today} icon={Calendar} color="#f59e0b" bgColor="#fed7aa" />
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search notifications..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select className="filter-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            <option value="order">Orders</option>
            <option value="seller">Sellers</option>
            <option value="review">Reviews</option>
            <option value="inventory">Inventory</option>
            <option value="payout">Payouts</option>
            <option value="payment">Payments</option>
            <option value="shipping">Shipping</option>
            <option value="message">Messages</option>
            <option value="system">System</option>
          </select>
          <select className="filter-select" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
            <option value="all">All Priority</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          {selectedNotifications.length > 0 && (
            <button className="btn-delete-selected" onClick={deleteSelected}>
              <Trash2 size={16} />
              Delete Selected ({selectedNotifications.length})
            </button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="notifications-container">
        <div className="notifications-list">
          {/* Select All Header */}
          <div className="list-header">
            <label className="checkbox-label">
              <input 
                type="checkbox" 
                checked={selectedNotifications.length === paginatedNotifications.length && paginatedNotifications.length > 0}
                onChange={selectAll}
              />
              <span>Select All</span>
            </label>
            <div className="list-header-info">
              {filteredNotifications.length} notifications found
            </div>
          </div>

          {/* Notifications */}
          {paginatedNotifications.map((notification) => {
            const typeConfig = getTypeConfig(notification.type);
            const priorityConfig = getPriorityConfig(notification.priority);
            const TypeIcon = typeConfig.icon;
            const PriorityIcon = priorityConfig.icon;
            const NotificationIcon = notification.icon;
            
            return (
              <div key={notification.id} className={`notification-item ${!notification.read ? 'unread' : ''}`}>
                <div className="notification-checkbox">
                  <input 
                    type="checkbox" 
                    checked={selectedNotifications.includes(notification.id)}
                    onChange={() => toggleSelectNotification(notification.id)}
                  />
                </div>
                <div className="notification-icon" style={{ backgroundColor: typeConfig.bg }}>
                  <NotificationIcon size={20} color={typeConfig.color} />
                </div>
                <div className="notification-content">
                  <div className="notification-header">
                    <div className="notification-title-section">
                      <h4 className="notification-title">{notification.title}</h4>
                      {!notification.read && <span className="unread-badge">New</span>}
                    </div>
                    <div className="notification-time">
                      <Clock size={12} />
                      <span>{notification.time}</span>
                      <span className="date-separator">•</span>
                      <span>{notification.date}</span>
                    </div>
                  </div>
                  <p className="notification-message">{notification.message}</p>
                  <div className="notification-footer">
                    <div className="notification-badges">
                      <span className="type-badge" style={{ backgroundColor: typeConfig.bg, color: typeConfig.color }}>
                        <TypeIcon size={10} />
                        {typeConfig.label}
                      </span>
                      <span className="priority-badge" style={{ backgroundColor: priorityConfig.bg, color: priorityConfig.color }}>
                        <PriorityIcon size={10} />
                        {priorityConfig.label}
                      </span>
                    </div>
                    <div className="notification-actions">
                      {!notification.read && (
                        <button className="action-btn read" onClick={() => markAsRead(notification.id)}>
                          <Eye size={14} />
                          Mark as Read
                        </button>
                      )}
                      {notification.actionLink && (
                        <Link to={notification.actionLink} className="action-btn view">
                          <Eye size={14} />
                          View Details
                        </Link>
                      )}
                      <button className="action-btn delete" onClick={() => deleteNotification(notification.id)}>
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-wrapper">
            <div className="pagination-info">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredNotifications.length)} of {filteredNotifications.length} notifications
            </div>
            <div className="pagination-buttons">
              <button 
                className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i}
                  className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredNotifications.length === 0 && (
          <div className="empty-state">
            <Bell size={64} className="empty-icon" />
            <h3>No notifications</h3>
            <p>You're all caught up! No new notifications to display.</p>
          </div>
        )}
      </div>

      
    </div>
  );
};

export default Notifications;